import { type FC, useMemo, useRef } from 'react';

import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { UnorderedList } from '@vakantiesnl/components/src/atoms/UnorderedList';
import { ImageSlider } from '@vakantiesnl/components/src/molecules/ImageSlider';
import { ModalOrDrawer } from '@vakantiesnl/components/src/molecules/ModalOrDrawer/ModalOrDrawer';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';

import { useStyles } from './RoomInfoModal.styles';

type RoomInfoModalProps = {
	title: string;
	open: boolean;
	onClose: VoidFunction;
	images: { url: string }[] | null;
	attributes: string[];
	loader?: false | 'CloudImage' | 'Contentful';
};

export const RoomInfoModal: FC<RoomInfoModalProps> = ({ attributes, title, open, onClose, images, loader }) => {
	const { isDesktop } = useBreakpoints();
	const microCopies = useMicroCopyContext();
	const { classes } = useStyles();
	/** Add focus to allow user input with arrows */
	const carouselRef = useRef<null | HTMLDivElement>(null);

	/**
	 * Temp split list so it looks better and the content is shorter.
	 * We might want to revisit this when adding headings to the list subjects
	 */
	const [leftAttributesList, rightAttributesList] = useMemo(() => {
		const midIndex = Math.ceil(attributes.length / 2);

		return [attributes.slice(0, midIndex), attributes.slice(midIndex)];
	}, [attributes]);

	return (
		<ModalOrDrawer title={microCopies['information']} open={open} size="large" onClose={onClose} hasCloseIcon>
			<div className={classes.roomInfoModalContainer}>
				<div className={classes.topContainer}>
					{!!images?.length && (
						<div className={classes.imageSliderContainer}>
							<ImageSlider
								images={images}
								alt="roomtype-images"
								imageClassName={classes.imageWrapper}
								containerClassName={classes.imageContainer}
								carouselRef={carouselRef}
								loader={loader}
							/>
							<div className={classes.imageCountContainer}>
								<Tag size="medium" color="black" leadingIcon="image">
									{images.length}
								</Tag>
							</div>
						</div>
					)}
					<div className={classes.roomDetails}>
						<Typography
							as="h3"
							variant={isDesktop ? 'headingMd' : 'headingSm'}
							className={classes.roomTitle}
						>
							{title}
						</Typography>
					</div>
				</div>

				{!!attributes.length && (
					<div data-cy="room-type-modal-content" className={classes.attributeContainer}>
						<Typography as="h3" variant="headingSm">
							{microCopies['acco.facts.title']}
						</Typography>
						<div className={classes.attributesLists}>
							<UnorderedList items={leftAttributesList} customClassName={classes.attributesList} />
							<UnorderedList items={rightAttributesList} customClassName={classes.attributesList} />
						</div>
					</div>
				)}

				<div className={classes.disclaimer}>{microCopies['acco.roomInfo.disclaimer']}</div>
			</div>
		</ModalOrDrawer>
	);
};
